/**
 * THE TEXT-AND-STATE SURFACES, migrated onto @ankr/glass.
 *
 * These are the half that comes almost free once the design system exists: a table, a list, a set of
 * computed figures. The canvas surfaces — the drawing pane, the 3D atlas, the 2D↔3D switch — are
 * deliberately NOT here. They are the half that decides whether this feels world-class and they get
 * their own run rather than being swept along with a framework migration.
 *
 * ── WHAT IS REAL HERE AND WHAT IS NOT ─────────────────────────────────────────────────────────
 * Every figure below comes from the live API. Nothing is mocked, and nothing is dressed up.
 *
 * The API returns bare values rather than provenance envelopes, so `normalise()` classifies them as
 * COMPUTED — which is honest, because they are computed from each hull's declared offsets. It would
 * be easy and wrong to wrap them in `cited(...)` here to make the chips look richer: a citation
 * invented in the presentation layer is exactly the failure the contract exists to prevent. When the
 * engines start returning envelopes, these surfaces gain real CITED chips with no change to a
 * component — that is the point of reading the contract rather than sniffing the payload.
 */

import { useState } from 'react';
import { AutoTable, Value, useWidgetData }
  from '../../../packages/ankr-glass/src/react.jsx';
import { plan } from '../../../packages/ankr-vahana/src/plan.mjs';

/**
 * SAME ORIGIN BY DEFAULT, and that is the architecture rather than a workaround.
 *
 * In production this app is served FROM mari8x.com, so `/api/fleet` is same-origin and no CORS is
 * involved. Hard-coding `https://mari8x.com` here made every call cross-origin, the API sends no
 * Access-Control-Allow-Origin, and the browser blocked all of it — the surfaces correctly rendered
 * `failed: Failed to fetch` rather than an empty table claiming there are no ships, which is how
 * this was caught. Local dev proxies /api through Vite (see vite.config.js) so the same relative
 * URLs work in both places and nothing has to change between them.
 */
/* THE API BASE FOLLOWS THE MOUNT PATH (2026-08-12).
   serve.mjs mounts this app at several whole-page paths (/study, /construction, /draw …) and nginx
   proxies each of those to :4904. It does NOT proxy /api — on mari8x.com that path belongs to a
   DIFFERENT service on :4051 — so a bare fetch('/api/…') from a page at /study left the origin and
   404'd. The shell loaded and nothing in it could fetch, which is exactly what "seems incomplete"
   looks like from outside.
   So the base is the mount segment the page is actually being served from, and the API lives under
   it (/study/api/…). VITE_API still overrides, for a build served from somewhere else entirely. */
const MOUNTS = ['study', 'construction', 'draw', 'learn', 'practice', 'sea', 'pastpapers'];
const seg = typeof location !== 'undefined' ? (location.pathname.split('/')[1] || '') : '';
export const API = import.meta.env?.VITE_API ?? (MOUNTS.includes(seg) ? `/${seg}` : '');

/**
 * Widget definitions in the shape widget-forge emits, so these surfaces are already speaking the
 * compiler's language. When the forge generates them for real, the definitions get deleted from here
 * and imported instead — the components do not change.
 */
const W = {
  fleet:   { slug: 'fleet-comparison', layout: 'auto-table',
             wire: { kind: 'rest', route: '/api/fleet', method: 'GET', field: 'fleet' } },
  girder:  { slug: 'hull-girder-section', layout: 'auto-table',
             wire: { kind: 'rest', route: '/api/hull-girder', method: 'GET', field: 'hull_girder' } },
  sheets:  { slug: 'drawing-sheets', layout: 'auto-table',
             wire: { kind: 'rest', route: '/api/atlas/sheets', method: 'GET', field: 'sheets' } },
};

/** One place to render loading / refusal / failure, so no surface invents its own empty state. */
function Panel({ label, title, blurb, state, children }) {
  return (
    <div className="g-glass shell-panel g-rise">
      <div className="g-label">{label}</div>
      <h2 className="g-h2" style={{ margin: 'var(--g-2) 0 var(--g-3)' }}>{title}</h2>
      {blurb ? <p className="g-body" style={{ marginBottom: 'var(--g-3)' }}>{blurb}</p> : null}
      {state?.loading ? <div className="gl-empty g-body">Loading…</div>
        : state?.envelope ? <Value of={state.envelope} />
        : children}
    </div>
  );
}

/** Every surface below is scoped to one ship, so the picker is shared rather than repeated. */
function ShipPicker({ ships, value, onChange }) {
  if (!ships?.length) return null;
  return (
    <div className="shell-picker">
      {ships.map((s) => (
        <button key={s.key} onClick={() => onChange(s.key)}
          className={`g-btn ${value === s.key ? 'g-btn-primary' : ''}`}>
          {s.ship?.replace(/^M\.[VT]\.\s*/, '') || s.key}
        </button>
      ))}
    </div>
  );
}

/**
 * THE FLEET — every declared hull, each with her own table of offsets.
 *
 * THE COUNT IS COUNTED, NEVER STATED. This title read "Seven hulls" while the fleet had grown to
 * eight. A hardcoded number in a HEADER outlives the commit that was true when it was written, and
 * is read by far more people than any comment explaining it.
 *
 * This is the surface the whole product rests on: every drawing, every hydrostatic figure and every
 * marked answer is computed from these hulls, which exist precisely BECAUSE the published ones are
 * under copyright we do not hold.
 */
export function Fleet({ onPick, picked }) {
  const st = useWidgetData(W.fleet, {}, { baseUrl: API, plan });
  const ships = st.data?.ships || [];

  // Columns are named rather than derived here: the raw payload carries a dozen keys and a table
  // that shows everything shows nothing. autoTable still derives them when nothing is specified.
  const rows = ships.map((s) => ({
    ship: s.ship, type: s.type,
    LBP_m: s.LBP, breadth_m: s.B, depth_m: s.D, Cb: s.Cb,
  }));

  return (
    <>
      <Panel label="The fleet · computed from declared offsets"
        title={`${ships.length || ''} hulls that belong to the project`.trim()}
        blurb="Every drawing and every hydrostatic figure in this course is integrated from these
               hulls' own tables of offsets. That is why they were drawn: a compulsory Hindship
               question sits in 40 of the 56 past papers reviewed, and its particulars are under a
               copyright we do not hold and will not reproduce."
        state={st}>
        <AutoTable widget={W.fleet} rows={rows} empty="No ships returned." />
        <div style={{ marginTop: 'var(--g-3)' }}>
          <ShipPicker ships={ships} value={picked} onChange={onPick} />
        </div>
      </Panel>
    </>
  );
}

/**
 * THE HULL GIRDER — the clearest case of a COMPUTED fact in the whole course.
 *
 * Change a plate thickness and the section modulus changes. Nothing here is looked up.
 */
export function HullGirder({ ship = 'abhyas' }) {
  const st = useWidgetData(W.girder, { ship }, { baseUrl: API, plan });
  const els = st.data?.elements || [];
  const rows = els.map((e) => ({
    element: e.name, t_mm: e.t_mm, length_m: e.length_m,
    area_cm2: e.area_cm2 ?? e.area, lever_m: e.lever_m ?? e.z,
  }));
  return (
    <Panel label="Hull girder · midship section"
      title={st.data?.ship || 'Section elements'}
      blurb="Every row is derived from the hull's declared geometry. Alter a thickness and the
             section modulus moves with it — there is no table being consulted."
      state={st}>
      <AutoTable widget={W.girder} rows={rows} empty="No elements for this ship." />
    </Panel>
  );
}

/**
 * THE SHEETS — which drawings a candidate can actually be examined on for this ship.
 *
 * A list, deliberately: opening one is a CANVAS surface and belongs to the later run.
 */
export function Sheets({ ship = 'abhyas' }) {
  const st = useWidgetData(W.sheets, { ship }, { baseUrl: API, plan });
  const list = st.data?.in_ship || [];
  const rows = list.map((d) => ({ drawing: d.title || d.drawing, id: d.drawing, tier: d.tier ?? null }));
  return (
    <Panel label="Markable drawings"
      title={`${list.length} sheet${list.length === 1 ? '' : 's'} for ${st.data?.ship_name || ship}`}
      blurb="Each of these can be sketched on screen and marked against the candidate's own
             coordinates — a correct answer drawn small in a corner still scores."
      state={st}>
      <AutoTable widget={W.sheets} rows={rows} empty="No sheets for this ship." />
    </Panel>
  );
}

/** LEARN — the fleet, then the two surfaces that hang off whichever ship is chosen. */
export function Learn() {
  const [ship, setShip] = useState('abhyas');
  return (
    <div className="shell-col">
      <Fleet picked={ship} onPick={setShip} />
      <HullGirder ship={ship} />
    </div>
  );
}

/** PRACTICE — what a candidate can be examined on, for the ship he picked. */
export function Practice() {
  const [ship, setShip] = useState('abhyas');
  const st = useWidgetData(W.fleet, {}, { baseUrl: API, plan });
  return (
    <div className="shell-col">
      <div className="g-glass shell-panel g-rise">
        <div className="g-label">Choose a vessel</div>
        <div style={{ marginTop: 'var(--g-3)' }}>
          <ShipPicker ships={st.data?.ships || []} value={ship} onChange={setShip} />
        </div>
      </div>
      <Sheets ship={ship} />
    </div>
  );
}
